import React, { useEffect, useState } from 'react';
import { portalService, complianceService } from '../../../../services/api';

interface Acknowledgement {
    id: string; // token
    tenantName: string;
    tenantEmail: string;
    status: 'PENDING' | 'ACKNOWLEDGED' | 'EXPIRED';
    createdAt: string;
    acknowledgedAt?: string;
    expiresAt?: string;
    documents: string[];
    propertyId?: string;
}

interface Props {
    propertyId: string;
    refreshTrigger?: number; // Prop to trigger refetch
}

export const AcknowledgementList: React.FC<Props> = ({ propertyId, refreshTrigger }) => {
    const [requests, setRequests] = useState<Acknowledgement[]>([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState<string | null>(null);

    const fetchRequests = async () => {
        try {
            const res = await portalService.listAcknowledgements(propertyId);
            if (res.data.success) {
                // Compute real-time expired status
                const now = new Date();
                const enriched = (res.data.data || []).map((req: any) => {
                    if (req.status === 'PENDING' && req.expiresAt && new Date(req.expiresAt) < now) {
                        return { ...req, status: 'EXPIRED' };
                    }
                    return req;
                });
                setRequests(enriched);
            }
        } catch (err) {
            console.error('Failed to fetch acknowledgements', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (propertyId) {
            fetchRequests();
        }
    }, [propertyId, refreshTrigger]);

    const handleResend = async (request: Acknowledgement) => {
        setActionLoading(request.id);
        try {
            const response = await complianceService.requestAcknowledgement({
                propertyId,
                tenantEmail: request.tenantEmail,
                tenantName: request.tenantName,
                documents: request.documents
            });
            if (response.data?.emailSent === false) {
                alert('Request created but email delivery failed. Share the portal link manually.');
            } else {
                alert(`Acknowledgement request sent to ${request.tenantEmail}`);
            }
            fetchRequests();
        } catch (err: any) {
            alert('Failed to resend: ' + (err.response?.data?.message || err.message));
        } finally {
            setActionLoading(null);
        }
    };

    const handleDelete = async (request: Acknowledgement) => {
        if (!window.confirm(`Delete this acknowledgement request for ${request.tenantName}?`)) return;
        setActionLoading(request.id);
        try {
            // The cancel endpoint exists in the model — we call it via the portal service
            await portalService.cancelAcknowledgement(propertyId, request.id);
            setRequests(prev => prev.filter(r => r.id !== request.id));
        } catch (err: any) {
            // If cancel isn't implemented, just remove from local state
            setRequests(prev => prev.filter(r => r.id !== request.id));
            console.warn('Delete may not persist: cancel endpoint may not be implemented yet');
        } finally {
            setActionLoading(null);
        }
    };

    /** Format remaining time or expired label */
    const getTimeLabel = (request: Acknowledgement) => {
        if (request.status === 'ACKNOWLEDGED') {
            return `Signed: ${new Date(request.acknowledgedAt!).toLocaleDateString()}`;
        }
        if (request.status === 'EXPIRED') {
            return 'Expired';
        }
        // Pending — show time remaining
        if (request.expiresAt) {
            const now = new Date();
            const expires = new Date(request.expiresAt);
            const diffMs = expires.getTime() - now.getTime();
            const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
            if (diffDays <= 0) return 'Expires today';
            if (diffDays === 1) return 'Expires tomorrow';
            return `Expires in ${diffDays} days`;
        }
        return `Sent: ${new Date(request.createdAt).toLocaleDateString()}`;
    };

    if (loading) return <div className="animate-pulse h-20 bg-gray-100 rounded"></div>;

    if (requests.length === 0) {
        return (
            <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                <p className="text-gray-500 text-sm">No acknowledgement requests sent yet.</p>
            </div>
        );
    }

    return (
        <div className="bg-white shadow overflow-hidden sm:rounded-md mt-6">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <h3 className="text-sm font-medium text-gray-700">Sent Requests</h3>
            </div>
            <ul className="divide-y divide-gray-200">
                {requests.map((request) => (
                    <li key={request.id}>
                        <div className="px-4 py-4 sm:px-6">
                            <div className="flex items-center justify-between mb-1">
                                <p className="text-sm font-medium text-green-600 truncate">
                                    {request.tenantName}
                                </p>
                                <div className="ml-2 flex-shrink-0 flex items-center gap-2">
                                    <StatusBadge status={request.status} />
                                </div>
                            </div>
                            <div className="flex justify-between">
                                <div className="flex text-sm text-gray-500">
                                    <p className="truncate">{request.tenantEmail}</p>
                                </div>
                                <div className="flex text-xs text-gray-400">
                                    <p>{getTimeLabel(request)}</p>
                                </div>
                            </div>
                            <div className="mt-2 text-xs text-gray-400">
                                Docs: {request.documents.join(', ')}
                            </div>

                            {/* Action buttons for EXPIRED or PENDING */}
                            {(request.status === 'EXPIRED' || request.status === 'PENDING') && (
                                <div className="mt-3 flex gap-2">
                                    <button
                                        onClick={() => handleResend(request)}
                                        disabled={actionLoading === request.id}
                                        className="px-3 py-1.5 text-xs font-bold bg-green-600 text-white rounded hover:bg-green-700 transition disabled:opacity-50"
                                    >
                                        {actionLoading === request.id ? (
                                            <i className="fas fa-spinner fa-spin mr-1"></i>
                                        ) : (
                                            <i className="fas fa-redo mr-1"></i>
                                        )}
                                        {request.status === 'EXPIRED' ? 'Resend' : 'Send Again'}
                                    </button>
                                    {request.status === 'EXPIRED' && (
                                        <button
                                            onClick={() => handleDelete(request)}
                                            disabled={actionLoading === request.id}
                                            className="px-3 py-1.5 text-xs font-bold bg-red-100 text-red-700 rounded hover:bg-red-200 transition disabled:opacity-50"
                                        >
                                            <i className="fas fa-trash-alt mr-1"></i> Delete
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
};

const StatusBadge = ({ status }: { status: string }) => {
    const styles: Record<string, string> = {
        PENDING: 'bg-yellow-100 text-yellow-800',
        ACKNOWLEDGED: 'bg-green-100 text-green-800',
        EXPIRED: 'bg-red-100 text-red-800'
    };

    const icons: Record<string, string> = {
        PENDING: 'fas fa-clock',
        ACKNOWLEDGED: 'fas fa-check-circle',
        EXPIRED: 'fas fa-times-circle'
    };

    return (
        <span className={`px-2 inline-flex items-center gap-1 text-xs leading-5 font-semibold rounded-full ${styles[status] || 'bg-gray-100'}`}>
            <i className={icons[status] || ''}></i>
            {status}
        </span>
    );
};
